import React, { useState, useEffect } from 'react';
import { useModal } from '../context/ModalDialogContext';

export default function AgentPortal({ user, tenantConfig, onNavigateToMint }) {
  const modal = useModal();
  const isDev = typeof window !== 'undefined' && window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');

  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'operators', 'invites', 'samples', 'commissions', 'chat'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // Overview KPIs
  const [overview, setOverview] = useState(null);

  // My Operators
  const [operators, setOperators] = useState([]);

  // Invitations
  const [invitations, setInvitations] = useState([]);
  const [inviteForm, setInviteForm] = useState({
    recipient_email: '',
    recipient_name: '',
    organization_name: '',
    personal_message: ''
  });
  const [sendingInvite, setSendingInvite] = useState(false);

  // Coupons
  const [coupons, setCoupons] = useState([]);

  // Commissions
  const [commissions, setCommissions] = useState([]);
  const [payoutDetails, setPayoutDetails] = useState({
    method: 'paypal',
    paypal_email: user?.agent_payout_details?.paypal_email || '',
    bank_name: user?.agent_payout_details?.bank_name || '',
    account_number: user?.agent_payout_details?.account_number || '',
    routing_swift: user?.agent_payout_details?.routing_swift || '',
    notes: user?.agent_payout_details?.notes || ''
  });

  // Chat with Operator
  const [chatContract, setChatContract] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMsgText, setNewMsgText] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const fetchHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'X-Tenant-Domain': window.location.hostname
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const loadAgentData = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Fetch Overview
      const resO = await fetch(`${API_BASE}/api/v1/agent-network/me/overview`, { headers: fetchHeaders });
      if (resO.ok) {
        setOverview(await resO.json());
      }

      // 2. Fetch Operators
      const resOp = await fetch(`${API_BASE}/api/v1/agent-network/me/operators`, { headers: fetchHeaders });
      if (resOp.ok) {
        setOperators(await resOp.json());
      }

      // 3. Fetch Invitations
      const resInv = await fetch(`${API_BASE}/api/v1/agent-network/invitations`, { headers: fetchHeaders });
      if (resInv.ok) {
        setInvitations(await resInv.json());
      }

      // 4. Fetch Coupons
      const resC = await fetch(`${API_BASE}/api/v1/agent-network/me/coupons`, { headers: fetchHeaders });
      if (resC.ok) {
        setCoupons(await resC.json());
      }

      // 5. Fetch Commissions
      const resComm = await fetch(`${API_BASE}/api/v1/agent-network/me/commissions`, { headers: fetchHeaders });
      if (resComm.ok) {
        setCommissions(await resComm.json());
      }
    } catch (err) {
      setError(err.message || 'Error loading agent dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAgentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Handlers ---

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setSendingInvite(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/invitations`, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify(inviteForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not send invitation.');
      showToast(data.message || 'Invitation sent successfully!');
      setInviteForm({
        recipient_email: '',
        recipient_name: '',
        organization_name: '',
        personal_message: ''
      });
      loadAgentData();
    } catch (err) {
      modal.alert('Invitation Error', err.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleSavePayoutDetails = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/v1/agent-network/me/payout-details`, {
        method: 'PUT',
        headers: fetchHeaders,
        body: JSON.stringify(payoutDetails)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update payout details.');
      showToast('Payout preferences saved.');
    } catch (err) {
      modal.alert('Error', err.message);
    }
  };

  const openChatWithOperator = async (op) => {
    setChatContract(op);
    setChatLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat/agent_contract/${op.contract_id}`, { headers: fetchHeaders });
      if (res.ok) {
        setChatMessages(await res.json());
      }
    } catch (err) {
      console.warn('Chat fetch error:', err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMsgText.trim() || !chatContract) return;
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat/agent_contract/${chatContract.contract_id}`, {
        method: 'POST',
        headers: fetchHeaders,
        body: JSON.stringify({ message_text: newMsgText.trim() })
      });
      if (res.ok) {
        const msg = await res.json();
        setChatMessages([...chatMessages, msg]);
        setNewMsgText('');
      }
    } catch (err) {
      modal.alert('Chat Error', err.message);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    showToast(`${label} copied to clipboard!`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header & Navigation */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid var(--border-color)',
        paddingBottom: '16px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>🤝</span>
            <span>Agent Partner Workspace</span>
          </h2>
          <div style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '4px' }}>
            Welcome, <strong>{user?.first_name || user?.email}</strong>. Sourced operators, contract earnings, monthly sample coupons, and client support.
          </div>
        </div>

        {/* Tab Selector */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', backgroundColor: 'rgba(255, 255, 255, 0.04)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'operators', label: `My Operators (${operators.length})` },
            { id: 'invites', label: 'Invite Operator' },
            { id: 'samples', label: `Sample Store (${coupons.filter(c => c.is_valid_now).length})` },
            { id: 'commissions', label: 'Earnings & Payouts' }
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === t.id ? 'var(--primary-color)' : 'transparent',
                color: activeTab === t.id ? '#ffffff' : 'var(--text-main)',
                transition: 'all 0.15s ease'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div style={{ fontSize: '12px', color: 'var(--text-mute)', fontStyle: 'italic' }}>
          Syncing agent portfolio data...
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          color: '#ef4444',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          ⚠ {error}
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          border: '1px solid rgba(16, 185, 129, 0.4)',
          color: '#10b981',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          ✓ {toast}
        </div>
      )}

      {/* Pending Account Notice */}
      {user?.agent_status === 'PENDING_APPROVAL' && (
        <div style={{
          padding: '14px 18px',
          borderRadius: '8px',
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          color: '#f59e0b',
          fontSize: '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>⏳</span>
          <div>
            <strong>Application Pending Review:</strong> Your agent profile has been received and is currently being reviewed by the tenant administrator. Once approved, you will be allocated your monthly sample coupons and invitation dispatch privileges.
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 1. OVERVIEW TAB                                      */}
      {/* ==================================================== */}
      {activeTab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '16px' }}>
            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Active Operators</div>
              <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--text-main)', marginTop: '4px' }}>
                {overview?.active_operators_count || 0}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Lodges & clubs under contract</div>
            </div>

            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Month-to-Date Earnings</div>
              <div style={{ fontSize: '26px', fontWeight: '800', color: '#10b981', marginTop: '4px' }}>
                ${(overview?.commission_mtd || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Accrued commissions this month</div>
            </div>

            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Lifetime Commission</div>
              <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--primary-color)', marginTop: '4px' }}>
                ${(overview?.commission_lifetime || 0).toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Total earnings to date</div>
            </div>

            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Sample Coupons Remaining</div>
              <div style={{ fontSize: '26px', fontWeight: '800', color: '#3b82f6', marginTop: '4px' }}>
                {overview?.available_coupons_count || 0}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Expires at end of current month</div>
            </div>

            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', textTransform: 'uppercase', fontWeight: '700' }}>Service Quality Rating</div>
              <div style={{ fontSize: '26px', fontWeight: '800', color: '#f59e0b', marginTop: '4px' }}>
                ★ {overview?.avg_qos_rating || 5.0}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-mute)', marginTop: '4px' }}>Client satisfaction rating</div>
            </div>
          </div>

          {/* Quick Actions & Recent Operators */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
            {/* Active Sample Code Pill */}
            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎟️</span>
                <span>Your Active Sample Coupon Codes</span>
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--text-mute)', margin: 0 }}>
                Use these single-use codes at checkout to void all product charges for physical demonstration coins ($20 courier shipping applies).
              </p>

              {coupons.filter(c => c.is_valid_now).length === 0 ? (
                <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-mute)', fontSize: '12px', backgroundColor: 'rgba(255, 255, 255, 0.02)', borderRadius: '6px' }}>
                  No active coupon codes available this month. New codes will be issued on the 1st of next month.
                </div>
              ) : (
                coupons.filter(c => c.is_valid_now).map(cp => (
                  <div key={cp.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 14px', borderRadius: '6px',
                    backgroundColor: 'rgba(59, 130, 246, 0.08)', border: '1px dashed #3b82f6'
                  }}>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontWeight: '800', fontSize: '14px', color: '#3b82f6' }}>{cp.code}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-mute)', marginTop: '2px' }}>Valid until {new Date(cp.expires_at).toLocaleDateString()}</div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(cp.code, 'Coupon code')}
                      style={{
                        padding: '5px 10px', borderRadius: '4px',
                        backgroundColor: '#3b82f6', border: 'none',
                        color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                      }}
                    >
                      Copy
                    </button>
                  </div>
                ))
              )}

              {onNavigateToMint && (
                <button
                  onClick={() => onNavigateToMint()}
                  style={{
                    padding: '8px 14px', borderRadius: '6px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-color)',
                    color: 'var(--text-main)', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textAlign: 'center'
                  }}
                >
                  Go to Store to Order Samples →
                </button>
              )}
            </div>

            {/* Invite Callout */}
            <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>✉️</span>
                <span>Onboard New Operators</span>
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--text-mute)', margin: 0 }}>
                Send official invitations directly to fishing lodges, hunting clubs, and reserves. Sourced operators will automatically be linked to your contract.
              </p>
              <button
                onClick={() => setActiveTab('invites')}
                style={{
                  padding: '10px 16px', borderRadius: '6px',
                  backgroundColor: 'var(--primary-color)', border: 'none',
                  color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer'
                }}
              >
                + Send Operator Invitation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 2. MY OPERATORS TAB                                  */}
      {/* ==================================================== */}
      {activeTab === 'operators' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>My Sourced Operators</h3>
            <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
              Organizations linked to your active partner contract. You earn recurring commission on all their product orders and hosting renewals.
            </div>
          </div>

          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Organization</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Main Contact</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Contract Expiry</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>Orders</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Total Spend</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {operators.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{ padding: '36px', textAlign: 'center', color: 'var(--text-mute)' }}>
                      You haven't onboarded any operators yet. Click "Invite Operator" to send your first partner invitation!
                    </td>
                  </tr>
                ) : (
                  operators.map(op => (
                    <tr key={op.contract_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{op.organization_name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>Contract #{op.contract_id} ({op.commission_rate_pct}%)</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{op.contact_name || 'Operator'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>{op.contact_email}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                          backgroundColor: op.is_active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          color: op.is_active ? '#10b981' : '#ef4444'
                        }}>
                          {op.is_active ? `Active (${new Date(op.end_date).toLocaleDateString()})` : 'Expired'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '700' }}>
                        {op.total_orders_placed}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', color: '#10b981' }}>
                        ${op.total_spend.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => openChatWithOperator(op)}
                          style={{
                            padding: '6px 12px', borderRadius: '6px',
                            backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#3b82f6', fontSize: '11px', fontWeight: '600', cursor: 'pointer'
                          }}
                        >
                          💬 Direct Message
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 3. INVITE OPERATOR TAB                               */}
      {/* ==================================================== */}
      {activeTab === 'invites' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Invitation Form Card */}
          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>Send Operator Invitation</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', marginTop: '4px' }}>
              The recipient will receive an official branded invitation email with an onboarding wizard link that automatically registers them as an Operator, creates their Organization, and binds them to your contract terms.
            </p>

            <form onSubmit={handleSendInvite} style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>
                    Client Email Address: *
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteForm.recipient_email}
                    onChange={(e) => setInviteForm({ ...inviteForm, recipient_email: e.target.value })}
                    placeholder="manager@lodge.com"
                    style={{
                      width: '100%', padding: '10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>
                    Contact Person Name:
                  </label>
                  <input
                    type="text"
                    value={inviteForm.recipient_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, recipient_name: e.target.value })}
                    placeholder="John Doe"
                    style={{
                      width: '100%', padding: '10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>
                    Organization / Lodge Name:
                  </label>
                  <input
                    type="text"
                    value={inviteForm.organization_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, organization_name: e.target.value })}
                    placeholder="Serengeti Safari Lodge"
                    style={{
                      width: '100%', padding: '10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>
                  Personal Note (Included in Email):
                </label>
                <textarea
                  rows={2}
                  value={inviteForm.personal_message}
                  onChange={(e) => setInviteForm({ ...inviteForm, personal_message: e.target.value })}
                  placeholder="It was great meeting you at the game fair! Here is the invite to set up your official TrophyCoin registry..."
                  style={{
                    width: '100%', padding: '10px', borderRadius: '6px',
                    backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                    border: '1px solid var(--border-color)', color: 'var(--text-main)'
                  }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                <button
                  type="submit"
                  disabled={sendingInvite}
                  style={{
                    padding: '10px 20px', borderRadius: '6px',
                    backgroundColor: 'var(--primary-color)', border: 'none',
                    color: '#fff', fontSize: '13px', fontWeight: '700',
                    cursor: sendingInvite ? 'not-allowed' : 'pointer'
                  }}
                >
                  {sendingInvite ? 'Dispatching...' : 'Dispatch Invitation Email 🚀'}
                </button>
              </div>
            </form>
          </div>

          {/* Invitation Tracker Table */}
          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', overflowX: 'auto' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)', fontWeight: '700' }}>
              Invitation Activity & Tracking
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Recipient</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Organization</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Sent Date</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Invite Link</th>
                </tr>
              </thead>
              <tbody>
                {invitations.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-mute)' }}>
                      No invitations dispatched yet.
                    </td>
                  </tr>
                ) : (
                  invitations.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{inv.recipient_name || 'Invitee'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>{inv.recipient_email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', color: 'var(--text-main)' }}>
                        {inv.organization_name || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '12px', color: 'var(--text-mute)' }}>
                        {new Date(inv.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                          backgroundColor:
                            inv.status === 'ACCEPTED' ? 'rgba(16, 185, 129, 0.15)' :
                            inv.status === 'PENDING' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          color:
                            inv.status === 'ACCEPTED' ? '#10b981' :
                            inv.status === 'PENDING' ? '#f59e0b' : '#ef4444'
                        }}>
                          {inv.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/register?invite=${inv.invite_token}`;
                            copyToClipboard(url, 'Invite link');
                          }}
                          style={{
                            padding: '4px 10px', borderRadius: '4px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-color)',
                            color: 'var(--text-main)', fontSize: '11px', cursor: 'pointer'
                          }}
                        >
                          Copy URL
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 4. SAMPLE STORE & COUPONS TAB                        */}
      {/* ==================================================== */}
      {activeTab === 'samples' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>Monthly Sample Coupons</h3>
            <div style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '2px' }}>
              Your contract provides free demonstration samples each calendar month. Apply your coupon code in the store to void 100% of product charges ($20 courier shipping applies).
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {coupons.map(c => (
              <div
                key={c.id}
                style={{
                  backgroundColor: 'var(--panel-bg)',
                  border: `1px solid ${c.is_used ? 'var(--border-color)' : '#3b82f6'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  boxShadow: 'var(--card-shadow)',
                  opacity: c.is_used ? 0.6 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'monospace', fontWeight: '800', fontSize: '16px', color: c.is_used ? 'var(--text-mute)' : '#3b82f6' }}>
                    {c.code}
                  </div>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                    backgroundColor: c.is_used ? 'rgba(255, 255, 255, 0.05)' : 'rgba(16, 185, 129, 0.15)',
                    color: c.is_used ? 'var(--text-mute)' : '#10b981'
                  }}>
                    {c.is_used ? 'REDEEMED' : 'READY TO USE'}
                  </span>
                </div>

                <div style={{ fontSize: '12px', color: 'var(--text-mute)' }}>
                  Valid: {new Date(c.valid_from).toLocaleDateString()} &ndash; {new Date(c.expires_at).toLocaleDateString()}
                  {c.is_used && c.used_at && (
                    <div style={{ marginTop: '2px', color: '#10b981' }}>Used on {new Date(c.used_at).toLocaleDateString()} for Order #{c.order_id}</div>
                  )}
                </div>

                {!c.is_used && (
                  <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                    <button
                      onClick={() => copyToClipboard(c.code, 'Coupon code')}
                      style={{
                        flex: 1, padding: '6px', borderRadius: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-color)',
                        color: 'var(--text-main)', fontSize: '11px', fontWeight: '600', cursor: 'pointer'
                      }}
                    >
                      Copy Code
                    </button>
                    {onNavigateToMint && (
                      <button
                        onClick={() => onNavigateToMint(c.code)}
                        style={{
                          flex: 1, padding: '6px', borderRadius: '4px',
                          backgroundColor: '#3b82f6', border: 'none',
                          color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                        }}
                      >
                        Redeem in Store
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ==================================================== */}
      {/* 5. EARNINGS & PAYOUTS TAB                            */}
      {/* ==================================================== */}
      {activeTab === 'commissions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Payout Settings Card */}
          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '18px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-main)' }}>Payout Preferences</h3>
            <p style={{ fontSize: '12px', color: 'var(--text-mute)', marginTop: '4px' }}>
              Configure how you would like the tenant administration to disburse your accrued commission payments.
            </p>

            <form onSubmit={handleSavePayoutDetails} style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>PayPal Email Address:</label>
                  <input
                    type="email"
                    value={payoutDetails.paypal_email}
                    onChange={(e) => setPayoutDetails({ ...payoutDetails, paypal_email: e.target.value })}
                    placeholder="payments@agent.com"
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-mute)', display: 'block', marginBottom: '4px' }}>Bank Name / IBAN / Account:</label>
                  <input
                    type="text"
                    value={payoutDetails.account_number}
                    onChange={(e) => setPayoutDetails({ ...payoutDetails, account_number: e.target.value })}
                    placeholder="IBAN GB82 WEST 123456"
                    style={{
                      width: '100%', padding: '8px 10px', borderRadius: '6px',
                      backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                      border: '1px solid var(--border-color)', color: 'var(--text-main)'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px', borderRadius: '6px',
                    backgroundColor: 'var(--primary-color)', border: 'none',
                    color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer'
                  }}
                >
                  Save Payout Preferences
                </button>
              </div>
            </form>
          </div>

          {/* Commissions Statement Table */}
          <div style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', overflowX: 'auto' }}>
            <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)', fontWeight: '700' }}>
              Commission Statements & Order Earnings
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase' }}>Operator Organization</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Order Subtotal</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Commission</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '12px 16px', color: 'var(--text-mute)', fontSize: '11px', textTransform: 'uppercase', textAlign: 'right' }}>Payout Reference</th>
                </tr>
              </thead>
              <tbody>
                {commissions.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '36px', textAlign: 'center', color: 'var(--text-mute)' }}>
                      No commission earnings recorded yet.
                    </td>
                  </tr>
                ) : (
                  commissions.map(c => (
                    <tr key={c.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '12px 16px', color: 'var(--text-mute)' }}>
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: '600' }}>
                        {c.organization_name}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600' }}>
                        ${c.commissionable_subtotal.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: 'var(--text-mute)' }}>
                        {c.commission_pct}%
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '700', color: '#10b981' }}>
                        ${c.commission_amount.toFixed(2)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700',
                          backgroundColor: c.status === 'PAID' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                          color: c.status === 'PAID' ? '#10b981' : '#f59e0b'
                        }}>
                          {c.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: 'var(--text-mute)' }}>
                        {c.payout_reference || 'Pending Next Cycle'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- MODAL: In-App Direct Chat with Operator --- */}
      {chatContract && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--panel-bg)', border: '1px solid var(--border-color)',
            borderRadius: '12px', width: '90%', maxWidth: '520px', height: '560px',
            display: 'flex', flexDirection: 'column', overflow: 'hidden'
          }}>
            {/* Chat Header */}
            <div style={{
              padding: '16px', borderBottom: '1px solid var(--border-color)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)' }}>
                  Support: {chatContract.organization_name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-mute)' }}>
                  Operator Contact: {chatContract.contact_name} ({chatContract.contact_email})
                </div>
              </div>
              <button
                onClick={() => setChatContract(null)}
                style={{
                  background: 'none', border: 'none', color: 'var(--text-mute)',
                  fontSize: '18px', cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            {/* Chat Messages Timeline */}
            <div style={{
              flex: 1, padding: '16px', overflowY: 'auto',
              display: 'flex', flexDirection: 'column', gap: '12px'
            }}>
              {chatLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-mute)', margin: 'auto' }}>Loading messages...</div>
              ) : chatMessages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-mute)', margin: 'auto' }}>
                  No messages yet. Send a greeting to {chatContract.organization_name}!
                </div>
              ) : (
                chatMessages.map(m => {
                  const isMe = m.sender_id === user?.id;
                  return (
                    <div
                      key={m.id}
                      style={{
                        alignSelf: isMe ? 'flex-end' : 'flex-start',
                        maxWidth: '80%',
                        backgroundColor: isMe ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.06)',
                        color: isMe ? '#fff' : 'var(--text-main)',
                        padding: '10px 14px',
                        borderRadius: '10px'
                      }}
                    >
                      <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: '2px' }}>
                        {isMe ? 'You' : (m.sender?.first_name || 'Operator')} &bull; {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                        {m.message_text}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Chat Input Bar */}
            <form onSubmit={handleSendMessage} style={{
              padding: '12px 16px', borderTop: '1px solid var(--border-color)',
              display: 'flex', gap: '8px'
            }}>
              <input
                type="text"
                value={newMsgText}
                onChange={(e) => setNewMsgText(e.target.value)}
                placeholder="Type your reply to operator..."
                style={{
                  flex: 1, padding: '10px 12px', borderRadius: '6px',
                  backgroundColor: 'var(--input-bg, rgba(255,255,255,0.05))',
                  border: '1px solid var(--border-color)', color: 'var(--text-main)'
                }}
              />
              <button
                type="submit"
                style={{
                  padding: '10px 16px', borderRadius: '6px',
                  backgroundColor: 'var(--primary-color)', border: 'none',
                  color: '#fff', fontWeight: '700', cursor: 'pointer'
                }}
              >
                Send
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
